import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Modal, Label, errText } from "../portal/cms/cmsShared";

export const ROLE_LABEL = { platform_admin: "Platform Admin", admin: "Lodge Admin", member: "Member" };
export const CMS = ["admin", "author", "reviewer"];

export function Chip({ on, onClick, disabled, tid, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={tid}
      className={`rounded-full px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${on ? "bg-sapphire-deep text-parchment" : "bg-sapphire-deep/5 text-sapphire-deep/50 hover:bg-sapphire-deep/10"}`}>
      {children}
    </button>
  );
}

export function UserFormModal({ user, onClose, onSaved }) {
  const editing = !!user;
  const [f, setF] = useState(user ? { name: user.name, rank: user.rank || "", role: user.role, cms_roles: user.cms_roles || [] }
    : { name: "", email: "", rank: "Master Mason", role: "member", cms_roles: [], password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (r) => setF((s) => ({ ...s, cms_roles: s.cms_roles.includes(r) ? s.cms_roles.filter((x) => x !== r) : [...s.cms_roles, r] }));
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/platform/users/${user.id}`, f);
      else await api.post("/platform/users", f);
      onSaved(editing ? "User updated." : "User created.");
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  const fields = editing ? [["name", "Full name"], ["rank", "Rank / title"]] : [["name", "Full name"], ["email", "Email"], ["rank", "Rank / title"], ["password", "Initial password"]];
  return (
    <Modal title={editing ? `Edit ${user.name}` : "Create account"} onClose={onClose} testid="user-form-modal">
      <div className="grid gap-4">
        {fields.map(([k, l]) => (
          <label key={k}><Label>{l}</Label>
            <input className="input-lodge" type={k === "password" ? "password" : "text"} value={f[k]} onChange={up(k)} data-testid={`user-form-${k}`} />
          </label>
        ))}
        {!editing && <p className="-mt-2 font-sans text-[11px] text-stonegrey">At least 10 characters with letters and numbers.</p>}
        <label><Label>Platform role</Label>
          <select className="input-lodge" value={f.role} onChange={up("role")} data-testid="user-form-role">
            {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <div><Label>Content (CMS) roles</Label>
          <div className="flex gap-2">{CMS.map((r) => <Chip key={r} on={f.cms_roles.includes(r)} onClick={() => toggle(r)} tid={`user-form-cms-${r}`}>{r}</Chip>)}</div>
          {f.role !== "member" && <p className="mt-1.5 font-sans text-[11px] italic text-stonegrey">Admins already have full CMS access.</p>}
        </div>
      </div>
      {err && <p className="mt-3 font-sans text-sm text-garnet" data-testid="user-form-error">{err}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onClose} className="btn-dark">Cancel</button>
        <button onClick={save} disabled={busy || !f.name || (!editing && (!f.email || !f.password))} className="btn-sapphire" data-testid="user-form-save">
          {busy && <Loader2 size={15} className="animate-spin" />} {editing ? "Save changes" : "Create account"}
        </button>
      </div>
    </Modal>
  );
}

export function PasswordModal({ user, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const save = async () => {
    try { await api.post(`/platform/users/${user.id}/password`, { password: pw }); onSaved(`Password reset for ${user.name}.`); }
    catch (e) { setErr(errText(e)); }
  };
  return (
    <Modal title={`Reset password · ${user.name}`} onClose={onClose} testid="password-modal">
      <input type="password" className="input-lodge" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="password-input" />
      <p className="mt-1.5 font-sans text-[11px] text-stonegrey">At least 10 characters with letters and numbers. Also clears any sign-in lockout.</p>
      {err && <p className="mt-3 font-sans text-sm text-garnet" data-testid="password-error">{err}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onClose} className="btn-dark">Cancel</button>
        <button onClick={save} disabled={!pw} className="btn-sapphire" data-testid="password-save">Reset password</button>
      </div>
    </Modal>
  );
}
